import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Download,
  RefreshCw,
  Trash2,
  RotateCcw,
  Clock,
  Database,
  FileText,
  Calendar,
  HardDrive,
  AlertCircle,
  CheckCircle,
  Loader2,
  Archive,
  Plus
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export function BackupsView({ currentProject, onDataRefresh }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Fetch backups list
  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/backups?project=${encodeURIComponent(currentProject)}`);
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Erreur lors du chargement des sauvegardes');
    } finally {
      setLoading(false);
    }
  }, [currentProject]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  // Create manual backup
  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      const res = await fetch(`${API}/api/backups/create?project=${encodeURIComponent(currentProject)}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Sauvegarde créée: ${data.ticket_count} tickets`);
        fetchBackups();
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Export current project to CSV (direct download)
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const res = await fetch(`${API}/api/backups/export/${encodeURIComponent(currentProject)}`);
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      // Get filename from header
      const contentDisposition = res.headers.get('Content-Disposition');
      let filename = `${currentProject.replace(/ /g, '_')}.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Download file
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Export CSV téléchargé!');
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Download backup as CSV
  const handleDownloadBackup = async (backupId) => {
    try {
      const res = await fetch(`${API}/api/backups/${backupId}/download`);
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backupId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Backup téléchargé!');
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    }
  };

  // Restore backup
  const handleRestore = async (backupId, replaceExisting = true) => {
    if (!window.confirm(
      replaceExisting 
        ? '⚠️ ATTENTION: Cela va REMPLACER tous les tickets actuels du projet par ceux du backup. Continuer?'
        : 'Les tickets du backup seront AJOUTÉS aux tickets existants (peut créer des doublons). Continuer?'
    )) return;

    try {
      setRestoring(backupId);
      const res = await fetch(`${API}/api/backups/${backupId}/restore?replace_existing=${replaceExisting}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Restauration réussie: ${data.restored_count} tickets`);
        if (onDataRefresh) onDataRefresh();
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setRestoring(null);
    }
  };

  // Delete backup
  const handleDelete = async (backupId) => {
    if (!window.confirm(`Supprimer la sauvegarde ${backupId}?`)) return;

    try {
      setDeleting(backupId);
      const res = await fetch(`${API}/api/backups/${backupId}`, { method: 'DELETE' });
      
      if (res.ok) {
        toast.success('Sauvegarde supprimée');
        fetchBackups();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setDeleting(null);
    }
  };

  // Format date
  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    const d = new Date(isoDate);
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format file size
  const formatSize = (kb) => {
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="backups-view">
      {/* Header Actions */}
      <div className="flex flex-wrap items-center gap-4">
        <Card className="flex-1 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <Archive className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Sauvegardes Automatiques</h3>
                  <p className="text-sm text-zinc-400">Toutes les heures, par projet</p>
                </div>
              </div>
              <Badge variant="outline" className="border-emerald-500/50 text-emerald-400">
                <CheckCircle className="w-3 h-3 mr-1" />
                Activé
              </Badge>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button
            onClick={handleExportCSV}
            disabled={exporting}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export CSV
          </Button>
          
          <Button
            onClick={handleCreateBackup}
            disabled={creating}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Nouvelle Sauvegarde
          </Button>
          
          <Button
            onClick={fetchBackups}
            variant="outline"
            disabled={loading}
            data-testid="refresh-backups-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-zinc-800/50 border-zinc-700">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            <div className="text-sm text-zinc-400">
              <p className="font-medium text-amber-400 mb-1">À propos des sauvegardes</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Export CSV</strong>: Télécharge instantanément les données actuelles du projet</li>
                <li><strong>Nouvelle Sauvegarde</strong>: Crée un point de restauration sur le serveur</li>
                <li><strong>Auto (vert)</strong>: Sauvegarde automatique (toutes les heures, max 24 conservées)</li>
                <li><strong>Manual (bleu)</strong>: Sauvegarde manuelle (jamais supprimée automatiquement)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card className="bg-zinc-800/80 border-zinc-700">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Database className="w-5 h-5 text-amber-400" />
              <CardTitle className="text-lg">Sauvegardes de {currentProject}</CardTitle>
            </div>
            <Badge variant="outline" className="text-zinc-400">
              {backups.length} sauvegarde(s)
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Archive className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune sauvegarde pour ce projet</p>
              <p className="text-sm mt-1">Créez votre première sauvegarde avec le bouton ci-dessus</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.backup_id}
                  className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-colors"
                  data-testid={`backup-item-${backup.backup_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${backup.type === 'auto' ? 'bg-emerald-500/20' : 'bg-blue-500/20'}`}>
                      {backup.type === 'auto' ? (
                        <Clock className="w-5 h-5 text-emerald-400" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-white">{backup.backup_id}</span>
                        <Badge 
                          variant="outline" 
                          className={backup.type === 'auto' 
                            ? 'border-emerald-500/50 text-emerald-400 text-xs' 
                            : 'border-blue-500/50 text-blue-400 text-xs'
                          }
                        >
                          {backup.type === 'auto' ? 'Auto' : 'Manual'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(backup.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Database className="w-3 h-3" />
                          {backup.ticket_count} tickets
                        </span>
                        <span className="text-emerald-400">{backup.achat_count} ACHAT</span>
                        <span className="text-amber-400">{backup.vente_count} VENTE</span>
                        <span className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          {formatSize(backup.size_kb)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadBackup(backup.backup_id)}
                      className="text-blue-400 border-blue-500/50 hover:bg-blue-500/20"
                      data-testid={`download-backup-${backup.backup_id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(backup.backup_id, true)}
                      disabled={restoring === backup.backup_id}
                      className="text-amber-400 border-amber-500/50 hover:bg-amber-500/20"
                      data-testid={`restore-backup-${backup.backup_id}`}
                    >
                      {restoring === backup.backup_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RotateCcw className="w-4 h-4" />
                      )}
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(backup.backup_id)}
                      disabled={deleting === backup.backup_id}
                      className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                      data-testid={`delete-backup-${backup.backup_id}`}
                    >
                      {deleting === backup.backup_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BackupsView;
